<template>
  <v-breadcrumbs :items="breadcrumbItems" density="compact" class="text-white">
    <template #divider>
      <v-icon size="small" :icon="mdiChevronRight" />
    </template>

    <template #item="{ item, index }">
      <v-breadcrumbs-item
        :disabled="item.disabled"
        :class="[item.disabled ? 'text-h6 breadcrumb-active' : 'text-body-2']"
        :title="item.disabled && !(item as BreadcrumbItem).loading ? item.title : undefined"
        :href="breadcrumbItems[index]?.to ? resolveHref(breadcrumbItems[index]!.to!) : undefined"
        @click="onBreadcrumbClick($event, breadcrumbItems[index]?.to)"
      >
        <template v-if="(item as BreadcrumbItem).loading">
          <v-skeleton-loader type="text" width="80" class="d-inline-block" />
        </template>
        <template v-else>
          {{ item.title }}
        </template>
      </v-breadcrumbs-item>
    </template>
  </v-breadcrumbs>
</template>

<script setup lang="ts">
import { computed, watch } from "vue"
import { useRoute, useRouter, type RouteLocationRaw } from "vue-router"
import { mdiChevronRight } from "@mdi/js"
import { useBreadcrumbContext } from "@/composables/useBreadcrumbs"
import { useMetaStore } from "@/stores/meta"

interface BreadcrumbItem {
  title: string
  to?: RouteLocationRaw
  disabled: boolean
  loading?: boolean
}

interface BreadcrumbMeta {
  title?: string
  parent?: string
  dynamicTitle?: boolean
  fallbackTitle?: string
}

interface RouteItem {
  name: string
  path: string
  meta?: {
    title?: string
    breadcrumb?: BreadcrumbMeta
  }
}

const route = useRoute()
const router = useRouter()
const metaStore = useMetaStore()
const breadcrumbContext = useBreadcrumbContext()

function buildBreadcrumbHierarchy(): RouteItem[] {
  const hierarchy: RouteItem[] = []
  let currentRouteName = route.name as string | undefined

  let iterations = 0
  const maxIterations = 10

  while (currentRouteName && iterations < maxIterations) {
    iterations++

    const routeRecord = router.getRoutes().find((r) => r.name === currentRouteName)
    if (!routeRecord) break

    const breadcrumbMeta = routeRecord.meta?.breadcrumb as BreadcrumbMeta | undefined

    hierarchy.unshift({
      name: currentRouteName,
      path: routeRecord.path,
      meta: {
        title: routeRecord.meta?.title as string | undefined,
        breadcrumb: breadcrumbMeta,
      },
    })

    currentRouteName = breadcrumbMeta?.parent
  }

  return hierarchy
}

function extractParamsForPath(pathPattern: string): Record<string, string> {
  const params: Record<string, string> = {}
  const pathParams = pathPattern.match(/:(\w+)/g) || []

  pathParams.forEach((param) => {
    const paramName = param.replace(":", "")
    const paramValue = route.params[paramName]
    if (paramValue) {
      params[paramName] = Array.isArray(paramValue) ? paramValue[0] : paramValue
    }
  })

  return params
}

const resolveHref = (to: RouteLocationRaw) => router.resolve(to).href

const onBreadcrumbClick = (event: MouseEvent, to?: RouteLocationRaw) => {
  if (!to) return
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return
  event.preventDefault()
  router.push(to)
}

const breadcrumbItems = computed<BreadcrumbItem[]>(() => {
  const contextTitle = breadcrumbContext?.dynamicTitle.value
  const contextLoading = breadcrumbContext?.dynamicLoading.value

  const items: BreadcrumbItem[] = []
  const hierarchy = buildBreadcrumbHierarchy()

  hierarchy.forEach((routeItem, index) => {
    const isLast = index === hierarchy.length - 1
    const breadcrumbMeta = routeItem.meta?.breadcrumb

    let title = breadcrumbMeta?.title || breadcrumbMeta?.fallbackTitle || routeItem.meta?.title || ""
    let loading = false

    if (isLast && breadcrumbMeta?.dynamicTitle && breadcrumbContext) {
      if (contextLoading) {
        title = breadcrumbMeta.fallbackTitle || title
        loading = true
      } else if (contextTitle) {
        title = contextTitle
      } else {
        title = breadcrumbMeta.fallbackTitle || title
      }
    }

    const routeLocation: RouteLocationRaw | undefined = isLast
      ? undefined
      : {
          name: routeItem.name,
          params: extractParamsForPath(routeItem.path),
        }

    items.push({
      title,
      to: routeLocation,
      disabled: isLast,
      loading,
    })
  })

  return items
})

watch(breadcrumbItems, (items) => {
  const lastItem = items.filter((i) => !!i.title && !i.loading).at(-1)
  metaStore.updateTitle(lastItem?.title || "Veraciti")
})
</script>

<style scoped lang="scss">
:deep(.breadcrumb-active) {
  max-width: 320px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: inline-block;
  vertical-align: middle;
}
</style>
